import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authStorage, Session } from 'functions/auth-session';

interface AuthContextValue {
    session: Session | null;
    authenticated: boolean;
    signIn: (session: Session) => void;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [session, setSession] = useState<Session | null>(() => authStorage.read());

    const signIn = useCallback((next: Session) => {
        authStorage.write(next);
        setSession(next);
    }, []);

    const signOut = useCallback(() => {
        authStorage.clear();
        setSession(null);
    }, []);

    // Outra aba pode ter entrado ou saído: o evento de storage sincroniza as duas.
    useEffect(() => {
        const revalidate = () => setSession(authStorage.read());

        window.addEventListener('storage', revalidate);

        return () => window.removeEventListener('storage', revalidate);
    }, []);

    const value = useMemo(
        () => ({ session, authenticated: session !== null, signIn, signOut }),
        [session, signIn, signOut]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) throw new Error('useAuth exige AuthProvider acima na árvore');

    return context;
};
